<template>
  <MarketCard :class="['list']" style="position: relative;">
    <CardHeader :eventId="eventId" />

    <div :class="classes.eventMarketWrapper">
      <EventName
        :class="[classes.eventName, 'event-name']"
        :marketId="marketId"
        :eventId="eventId"
        :useTeamNames="true"
      />
      <MarketName
        :marketId="marketId"
        :fallbackName="market.marketName"
        :class="['market-name', classes.marketName]"
      />
    </div>

    <ScrollableContainer
      :elementHeight="28"
      :gap="4"
      :px="10"
      :pb="12"
      @scroll.once="handleScroll"
    >
      <ListOption
        v-for="(option, i) in options"
        :key="option.id"
        :stagger="i"
        :eventId="eventId"
        :option="option"
        :marketId="market.marketId"
      />
    </ScrollableContainer>
  </MarketCard>
</template>

<script lang="ts">
import { createUseStyles } from "@fresh8/vue-jss/src";
import { computed, ComputedRef, defineComponent } from "vue";
import { injectBrand } from "@fresh8/ad-data-composition/src/brand";

import ListOption from "../ListOption.vue";
import EventName from "../../../EventNameBadges.vue";
import MarketName from "../../../MarketName.vue";
import CardHeader from "../../../CardHeader.vue";
import MarketCard from "../../../styled/MarketCard.vue";
import {
  composeMarket,
  composeListMarketOptions
} from "@fresh8/ad-data-composition/src/betData";
import { injectCarouselContext } from "../../../../lib/provideInjectors";
import { injectTemplateSize } from "@fresh8/ad-data-composition/src/useTemplateSize";
import { Market, Option } from "@fresh8/types/src/betData";
import ScrollableContainer from "../ScrollContainer.vue";

import { useHandleAnalytics } from "../../../../lib/analytics";
import { useHandleCardAnalytics } from "../../../../lib/card-analytics";
import { AnalyticEvents } from "@fresh8/types/src/analytics";
import { injectCardNumber } from "../../../../hooks/useAnalytics";

const useStyles = createUseStyles({
  eventMarketWrapper: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    paddingBottom: "8px",
    boxSizing: "border-box"
  },
  marketName: {
    margin: "0px 0",
    "$eventMarketWrapper &": {
      fontSize: "11px"
    }
  },
  eventName: {
    margin: "8px 0",
    alignSelf: "center",

    "& .competitors .banner": {
      maxWidth: "180px",
      "&.right": {
        marginLeft: props => (props.isCarousel ? "0" : "15px"),
        paddingRight: "8px"
      },
      "&.left": {
        marginRight: props => (props.isCarousel ? "0" : "15px"),
        paddingLeft: "8px"
      }
    },
    "& .vs": {
      placeSelf: "center",
      width: "25px"
    }
  }
});

export default defineComponent({
  name: "MPU",
  components: {
    ListOption,
    CardHeader,
    EventName,
    MarketCard,
    MarketName,
    ScrollableContainer
  },
  props: {
    marketId: { type: String, required: true },
    eventId: { type: String, required: true }
  },
  setup(props) {
    const brandContext = injectBrand();
    const carouselContext = injectCarouselContext();
    const templateSizeContext = injectTemplateSize();
    const $handleAnalytics = useHandleAnalytics();
    const $handleCardAnalytics = useHandleCardAnalytics();
    const cardNumber = injectCardNumber();

    const backgroundImage = computed(() => {
      return !!Object.keys(brandContext?.backgroundImageSets?.value || {})
        .length;
    });

    const relativeStyles = computed(() => ({
      ...(brandContext?.adTemplateBranding.value || {}),
      backgroundImage: backgroundImage.value,
      isCarousel: carouselContext.value.isCarousel
    }));

    const classesRef = useStyles(relativeStyles);
    const market = composeMarket(props.marketId) as ComputedRef<Market>;
    const marketOptions = composeListMarketOptions(
      props.marketId
    ) as ComputedRef<Option[]>;

    const handleScroll = () => {
      $handleAnalytics(AnalyticEvents.INTERACTED, "List Scroll");
      $handleCardAnalytics(
        AnalyticEvents.INTERACTED,
        "List Scroll",
        undefined,
        cardNumber
      );
    };

    return {
      handleScroll,
      layoutClass: computed(() => templateSizeContext?.size.value.name),
      market,
      marketBranding: brandContext?.marketBranding,
      classes: classesRef,
      options: marketOptions
    };
  }
});
</script>
